/**
 * Frontend mirror of the backend user-log model.
 * Must stay in sync with backend/src/model/user_log_model.ts and the
 * Glue table schema in backend/resources/user-log.yml.
 *
 * The backend controller (`POST /logs/batch`) accepts these records
 * via `navigator.sendBeacon()` or `fetch({ keepalive: true })`.
 */

export enum LogSource {
	FRONTEND = "frontend",
	BACKEND = "backend",
	ETC = "etc",
}

export enum LogResultStatus {
	SUCCESS = "success",
	FAIL = "fail",
}

/**
 * Canonical frontend action names. Extend as needed.
 * Backend-emitted actions (e.g. api_call, user_login, campaign_created) live
 * in the `backend` partition; frontend-emitted actions live in `frontend`.
 */
export enum LogAction {
	// Navigation
	PAGE_VIEW = "page_view",

	// Interaction — emit manually via useUserLog().logEvent() at key UI points.
	BUTTON_CLICK = "button_click",
	MODAL_OPEN = "modal_open",
	MODAL_CLOSE = "modal_close",
	SEARCH_SUBMIT = "search_submit",
	FILTER_CHANGED = "filter_changed",
	VIDEO_PLAY = "video_play",
	VIDEO_PAUSE = "video_pause",

	// Network / runtime failures observed client-side
	API_ERROR = "api_error",
	APP_ERROR = "app_error",

	// Auth UX events — complement backend user_login / user_register events
	// by capturing client-side attempts that never reach the server (network
	// failures, validation errors, user-aborted flows).
	USER_LOGIN_ATTEMPT = "user_login_attempt",
	USER_LOGIN_SUCCESS = "user_login_success",
	USER_LOGIN_FAILED = "user_login_failed",
	USER_REGISTER_ATTEMPT = "user_register_attempt",
	USER_REGISTER_FAILED = "user_register_failed",
	USER_LOGOUT = "user_logout",

	// Public landing and acquisition events
	LANDING_PAGE_VIEWED = "landing_page_viewed",
	LANDING_LOGIN_CLICKED = "landing_login_clicked",
	SIGNUP_PERSONA_SELECTED = "signup_persona_selected",
	CONTACT_FORM_OPENED = "contact_form_opened",
	CONTACT_FORM_SUBMITTED = "contact_form_submitted",
}

/**
 * Event as emitted from application code (via useUserLog().logEvent).
 * All fields are optional except `action` — the client fills in context
 * (user_id, device_id, page, timestamps, etc.) automatically.
 */
export interface UserLogEventInput {
	action: string;
	result_status?: LogResultStatus | null;
	error_code?: string | null;
	duration?: number | null;
	loading_time?: number | null;
	campaign_id?: string | null;
	block_id?: string | null;
	item_id?: string | null;
	/** Arbitrary extra context, JSON-serialized by the client before send. */
	properties?: Record<string, unknown> | null;
}

/**
 * Record shape actually pushed to the backend /logs/batch endpoint.
 * Matches backend UserLogInput — properties is pre-serialized to a string
 * so the backend treats it as opaque JSON.
 */
export interface UserLogRecordOut {
	source: LogSource;
	action: string;
	user_id: string | null;
	user_email: string | null;
	user_role: string | null;
	device_id: string | null;
	operating_system: string | null;
	browsing_environment: string | null;
	campaign_id: string | null;
	block_id: string | null;
	item_id: string | null;
	client_timestamp: string;
	duration: number | null;
	loading_time: number | null;
	result_status: string | null;
	error_code: string | null;
	page: string | null;
	properties: string | null;
}

export interface LogIdentity {
	userId: string | null;
	userEmail: string | null;
	userRoles: string[] | null;
}
